import React, { type ReactNode } from 'react';
import { graphql, usePreloadedQuery, type PreloadedQuery } from 'react-relay';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/index.tsx';
import type {
	JiraDeploymentsFeaturePrecondition,
	uiDeploymentsFeaturePreconditionQuery,
} from '@atlassian/jira-relay/src/__generated__/uiDeploymentsFeaturePreconditionQuery.graphql';
import { AsyncDeploymentsEmptyState } from '@atlassian/jira-software-deployments-empty-state/src/async.tsx';
import { useViewDeploymentsExperienceFail } from '@atlassian/jira-software-deployments/src/services/experience-tracker/index.tsx';
import { useViewDeploymentsTimelineExperienceFail } from '@atlassian/jira-software-deployments/src/services/timeline-experience-tracker';
import { useProjectDetails } from '../services/project-detail';
import { NotAvailablePage } from './not-available-page';
import type { PreconditionResult } from './types';
import { isNetworkError } from './utils';

export const getPrecondition = (data: PreconditionResult): JiraDeploymentsFeaturePrecondition => {
	if (!data.isFeatureEnabled) {
		return 'NOT_AVAILABLE';
	}
	if (!data.hasViewDevToolsPermission) {
		return 'NOT_AVAILABLE';
	}
	if (!data.hasIssuesWithDeployments) {
		return 'DEPLOYMENTS_EMPTY_STATE';
	}
	return 'ALL_SATISFIED';
};

export const PreconditionCheckOld = ({
	children,
	preconditionResult,
}: {
	children: ReactNode;
	preconditionResult: PreconditionResult;
}) => {
	const precondition = getPrecondition(preconditionResult);

	switch (precondition) {
		case 'ALL_SATISFIED':
			return <>{children}</>;
		case 'DEPLOYMENTS_EMPTY_STATE':
			return <AsyncDeploymentsEmptyState />;
		case 'NOT_AVAILABLE':
		default:
			return <NotAvailablePage />;
	}
};

export const PreconditionCheck = ({
	children,
	queryReference,
}: {
	children: ReactNode;
	queryReference: PreloadedQuery<uiDeploymentsFeaturePreconditionQuery>;
}) => {
	const project = useProjectDetails();
	const onViewDeploymentsExperienceFail = useViewDeploymentsExperienceFail();
	const onViewDeploymentsTimelineExperienceFail = useViewDeploymentsTimelineExperienceFail();
	const {
		jira: { deploymentsFeaturePreconditionByProjectKey },
	} = usePreloadedQuery(
		graphql`
			query uiDeploymentsFeaturePreconditionQuery($cloudId: ID!, $projectKey: String!) {
				jira @required(action: THROW) {
					deploymentsFeaturePreconditionByProjectKey(cloudId: $cloudId, projectKey: $projectKey)
						@required(action: THROW)
				}
			}
		`,
		queryReference,
	);

	let page;
	switch (deploymentsFeaturePreconditionByProjectKey) {
		case 'ALL_SATISFIED':
			page = children;
			break;
		case 'DEPLOYMENTS_EMPTY_STATE':
			page = <AsyncDeploymentsEmptyState />;
			break;
		case 'NOT_AVAILABLE':
		default:
			page = <NotAvailablePage />;
	}

	return (
		// this error boundary is placed here to check whether the project has deployments
		// originally this was done in spa-apps but that is no longer possible because we
		// need to access the precondition after usePreloadedQuery receives its query ref
		<JSErrorBoundary
			id="pageContent"
			packageName="jiraSoftwareDeploymentsPrecondition"
			teamName="fusion-solaris"
			extraEventData={{ isSimplifiedProject: project.isSimplified ?? false }}
			onError={(location: string, error: Error) => {
				onViewDeploymentsExperienceFail(location, error, {
					isNetworkError: isNetworkError(error),
					hasDeployments: deploymentsFeaturePreconditionByProjectKey === 'ALL_SATISFIED',
				});
				if (deploymentsFeaturePreconditionByProjectKey === 'ALL_SATISFIED') {
					// If the preconditions are satisfied, then we attempted to render the timeline
					onViewDeploymentsTimelineExperienceFail(location, error);
				}
			}}
			attributes={(error: Error) => ({
				isNetworkError: isNetworkError(error),
				isSimplifiedProject: project.isSimplified ?? false,
				hasDeployments: deploymentsFeaturePreconditionByProjectKey === 'ALL_SATISFIED',
			})}
		>
			{page}
		</JSErrorBoundary>
	);
};
